import { useQuery } from '@apollo/client/react';
import React, { useEffect, useMemo } from 'react';
import { Controller, Form, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  TrainingPlanEquipmentCategory,
  TrainingPlanFilterOptionsDocument,
  TrainingPlanTemplatesFilter,
  TrainingPlanType,
} from '../../../graphql/types';
import { TrackingEventData } from '../../../hooks/useTracking';
import { EventLabels, EventSubCategories } from '../../../utils/trackingEvents';
import Button, { ButtonVariant } from '../../reusable/Button';
import { OptionType, Select } from '../../reusable/Select';

const StyledFilterContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export type EquipmentCategoryFilter =
  | TrainingPlanEquipmentCategory
  | null
  | undefined;

const defaultValues = {
  difficulty: { value: undefined, label: 'Level' },
  durationMinutes: { value: undefined, label: 'Duration' },
  frequency: { value: undefined, label: 'Frequency' },
  phase: { value: undefined, label: 'Phase' },
  type: { value: undefined, label: 'Type' },
  equipment: { value: undefined, label: 'Equipment' },
};

interface TrainingPlanTemplateFiltersProps {
  setFilter: (filter: TrainingPlanTemplatesFilter) => void;
  trackingData?: TrackingEventData;
}

export const TrainingPlanTemplateFilters: React.FC<
  TrainingPlanTemplateFiltersProps
> = ({ setFilter, trackingData }) => {
  const { data: filterOptionsData } = useQuery(
    TrainingPlanFilterOptionsDocument,
  );

  const typeOptions: OptionType<TrainingPlanType | undefined>[] = useMemo(
    () => [
      defaultValues.type,
      ...(filterOptionsData?.trainingPlanTypes ?? []).map(
        ({ name, value }) => ({
          label: name,
          value,
        }),
      ),
    ],
    [filterOptionsData],
  );

  const difficultyOptions: OptionType<string | undefined>[] = useMemo(
    () => [
      defaultValues.difficulty,
      ...(filterOptionsData?.trainingPlanDifficulties ?? []).map(
        ({ name, value }) => ({ label: name, value }),
      ),
    ],
    [filterOptionsData],
  );

  const durationOptions: OptionType<number | undefined>[] = useMemo(
    () => [
      defaultValues.durationMinutes,
      ...(filterOptionsData?.trainingPlanDurations ?? []).map(
        ({ name, value }) => ({ label: name, value }),
      ),
    ],
    [filterOptionsData],
  );

  const equipmentOptions: OptionType<EquipmentCategoryFilter>[] = useMemo(
    () => [
      defaultValues.equipment,
      ...(filterOptionsData?.trainingPlanEquipmentCategories ?? []).map(
        ({ name, value }) => ({ label: name, value: value }),
      ),
    ],
    [filterOptionsData],
  );

  const frequencyOptions: OptionType<number | undefined>[] = useMemo(
    () => [
      defaultValues.frequency,
      ...(filterOptionsData?.trainingPlanFrequencies ?? []).map(
        ({ name, value }) => ({ label: name, value }),
      ),
    ],
    [filterOptionsData],
  );

  const phaseOptions: OptionType<number | undefined>[] = useMemo(
    () => [
      defaultValues.phase,
      ...(filterOptionsData?.trainingPlanPhases ?? []).map(
        ({ name, value }) => ({ label: name, value }),
      ),
    ],
    [filterOptionsData],
  );

  const { watch, control, reset } = useForm<{
    difficulty: OptionType<string>;
    durationMinutes: OptionType<number>;
    frequency: OptionType<number>;
    phase: OptionType<number>;
    type: OptionType<TrainingPlanType>;
    equipment: OptionType<TrainingPlanEquipmentCategory | null>;
  }>({
    defaultValues,
  });

  const difficulty = watch('difficulty');
  const durationMinutes = watch('durationMinutes');
  const frequency = watch('frequency');
  const phase = watch('phase');
  const type = watch('type');
  const equipment = watch('equipment');

  useEffect(() => {
    setFilter({
      ...(difficulty.value ? { difficulty: [difficulty.value] } : {}),
      ...(durationMinutes.value !== undefined
        ? { durationMinutes: [durationMinutes.value] }
        : {}),
      ...(frequency.value !== undefined
        ? { frequency: [frequency.value] }
        : {}),
      ...(phase.value !== undefined ? { phase: [phase.value] } : {}),
      ...(type.value !== undefined ? { type: [type.value] } : {}),
      ...(equipment.value !== undefined
        ? { equipmentCategory: [equipment.value] }
        : {}),
    });
  }, [
    difficulty,
    durationMinutes,
    frequency,
    phase,
    type,
    equipment,
    setFilter,
  ]);

  const trackingDefaults = {
    ...trackingData,
    event_subcategory: EventSubCategories.Filters,
  };

  return (
    <Form control={control}>
      <StyledFilterContainer>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={typeOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Type' }}
            />
          )}
        />
        <Controller
          control={control}
          name="difficulty"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={difficultyOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Level' }}
            />
          )}
        />
        <Controller
          control={control}
          name="durationMinutes"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={durationOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Duration' }}
            />
          )}
        />
        <Controller
          control={control}
          name="equipment"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={equipmentOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Equipment' }}
            />
          )}
        />
        <Controller
          control={control}
          name="frequency"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={frequencyOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Frequency' }}
            />
          )}
        />
        <Controller
          control={control}
          name="phase"
          render={({ field }) => (
            <Select
              {...field}
              multiple={false}
              options={phaseOptions}
              trackingData={{ ...trackingDefaults, event_label: 'Phase' }}
            />
          )}
        />

        <Button
          variant={ButtonVariant.Outline}
          onClick={() => reset(defaultValues)}
          trackingData={{
            ...trackingDefaults,
            event_label: EventLabels.Reset,
          }}
        >
          Reset
        </Button>
      </StyledFilterContainer>
    </Form>
  );
};
